import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { UiToastService } from './toast.service';

export type ToastPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right' | 'center';

export interface ToastItem {
  id: number;
  severity: 'success' | 'info' | 'warn' | 'error' | 'contrast' | 'secondary';
  summary: string;
  detail: string;
  position: ToastPosition;
  visible: boolean;
}

@Component({
  selector: 'app-toast',
  template: `
    <div [class]="containerClasses()" [attr.role]="'status'" [attr.aria-live]="'polite'">
      @for (toast of positionToasts(); track toast.id) {
        @if (toast.visible) {
          <div
            [class]="toastClasses(toast.severity)"
            [attr.role]="'alert'"
            [attr.aria-label]="toast.summary + ': ' + toast.detail"
          >
            <div class="flex items-start gap-3">
              <div class="flex-shrink-0 mt-0.5">
                @switch (toast.severity) {
                  @case ('success') { <i class="bi bi-check-circle-fill text-teal-500 text-lg" aria-hidden="true"></i> }
                  @case ('info') { <i class="bi bi-info-circle-fill text-accent-500 text-lg" aria-hidden="true"></i> }
                  @case ('warn') { <i class="bi bi-exclamation-triangle-fill text-amber-500 text-lg" aria-hidden="true"></i> }
                  @case ('error') { <i class="bi bi-x-circle-fill text-rose-500 text-lg" aria-hidden="true"></i> }
                  @default { <i class="bi bi-bell-fill text-slate-500 text-lg" aria-hidden="true"></i> }
                }
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold" [class]="summaryClasses(toast.severity)">{{ toast.summary }}</p>
                <p class="text-sm mt-0.5" [class]="detailClasses(toast.severity)">{{ toast.detail }}</p>
              </div>
              <button
                class="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 rounded"
                (click)="remove(toast.id)"
                aria-label="Cerrar notificación"
              >
                <i class="bi bi-x-lg" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        }
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UiToast {
  readonly position = input<ToastPosition>('top-right');

  private toastService = inject(UiToastService);

  protected readonly positionToasts = computed(() =>
    this.toastService.toasts().filter((t) => t.position === this.position())
  );

  protected containerClasses(): string {
    const base = 'fixed z-[100000] flex flex-col gap-2 pointer-events-none max-w-sm w-full px-4 sm:px-0';
    const positions: Record<ToastPosition, string> = {
      'top-left': 'top-4 left-4',
      'top-center': 'top-4 left-1/2 -translate-x-1/2',
      'top-right': 'top-4 right-4',
      'bottom-left': 'bottom-4 left-4',
      'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
      'bottom-right': 'bottom-4 right-4',
      'center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
    };
    return `${base} ${positions[this.position()]}`;
  }

  protected toastClasses(severity: string): string {
    const base = 'pointer-events-auto p-4 rounded-xl border shadow-lg animate-slide-up';
    const variants: Record<string, string> = {
      success: 'bg-teal-50 border-teal-200 text-teal-900',
      info: 'bg-accent-50 border-accent-200 text-accent-900',
      warn: 'bg-amber-50 border-amber-200 text-amber-900',
      error: 'bg-rose-50 border-rose-200 text-rose-900',
      contrast: 'bg-slate-800 border-slate-700 text-white',
      secondary: 'bg-slate-50 border-slate-200 text-slate-900',
    };
    return `${base} ${variants[severity] || variants['info']}`;
  }

  protected summaryClasses(severity: string): string {
    const variants: Record<string, string> = {
      success: 'text-teal-800',
      info: 'text-accent-800',
      warn: 'text-amber-800',
      error: 'text-rose-800',
      contrast: 'text-white',
      secondary: 'text-slate-800',
    };
    return variants[severity] || variants['info'];
  }

  protected detailClasses(severity: string): string {
    const variants: Record<string, string> = {
      success: 'text-teal-700',
      info: 'text-accent-700',
      warn: 'text-amber-700',
      error: 'text-rose-700',
      contrast: 'text-slate-300',
      secondary: 'text-slate-700',
    };
    return variants[severity] || variants['info'];
  }

  protected remove(id: number): void {
    this.toastService.remove(id);
  }
}
